import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { fetchBillingState, type BillingState } from "@/lib/billing";

/**
 * Loads the signed-in account's billing state.
 *
 * `refresh` exists because a Paddle checkout completes in the browser before
 * the webhook has been delivered and processed. The UI polls with `refresh`
 * rather than assuming the checkout callback means the account is upgraded.
 */
export function useBilling() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<BillingState | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setState(await fetchBillingState(user.id));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    void load();
  }, [authLoading, load]);

  return { billing: state, loading: authLoading || loading, refresh: load };
}
